// src/Pages/AuthPages/ResetPassword.jsx
import React, { useState, useMemo, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import icon from "../../Images/change-password-icon.png";
import LoadingScreen from "../../ReusableBlocks/LoadingScreen";
import {
  changePasswordByUrl,
  resetChangePasswordByUrl,
} from "../../Redux/Auth/ChangePasswordByUrlAction";

const ResetPassword = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const pathParams = useParams();               // legacy support
  const [searchParams] = useSearchParams();     // new format

  // Prefer query params; fallback to path params
  const uid = useMemo(
    () => searchParams.get("uid") || pathParams.uid || "",
    [searchParams, pathParams]
  );

  // --- NEW: sanitize token -> only use part before first underscore
  const rawToken = useMemo(
    () => searchParams.get("token") || pathParams.token || "",
    [searchParams, pathParams]
  );

  const parsedToken = useMemo(() => {
    if (!rawToken) return "";
    try {
      const decoded = decodeURIComponent(rawToken);
      // e.g. "cxc4fl-60b6ee...__ ;!!PEZB...$" -> take "cxc4fl-60b6ee..."
      return decoded.split("_")[0] || "";
    } catch {
      // if token had bad encoding, still do a best-effort split
      return String(rawToken).split("_")[0] || "";
    }
  }, [rawToken]);

  const { loading, success, error, expired } =
    useSelector((s) => s.ChangePasswordByUrl) || {};

  const [newPassword, setNewPassword] = useState("");
  const [cnewPassword, setCnewPassword] = useState("");
  const [validation, setValidation] = useState({
    length: false, uppercase: false, number: false, specialChar: false,
  });
  const [localError, setLocalError] = useState("");

  useEffect(() => {
    return () => dispatch(resetChangePasswordByUrl());
  }, [dispatch]);

  const handlePasswordChange = (e) => {
    const v = e.target.value;
    setNewPassword(v);
    setValidation({
      length: v.length >= 8,
      uppercase: /[A-Z]/.test(v),
      number: /[0-9]/.test(v),
      specialChar: /[!@#$%^&*_-]/.test(v),
    });
  };

  const disabled =
    !validation.length ||
    !validation.uppercase ||
    !validation.number ||
    !validation.specialChar ||
    !newPassword ||
    newPassword !== cnewPassword;

  const handleSubmit = (e) => {
    e.preventDefault();
    setLocalError("");

    if (!uid || !parsedToken) {
      // drive "expired/invalid" branch
      dispatch(changePasswordByUrl({ uid: "", token: "", password: "x", cnewpassword: "x" }));
      return;
    }

    if (disabled) {
      setLocalError("Please meet the password rules and confirm password correctly.");
      return;
    }

    // IMPORTANT: send parsedToken, not rawToken
    dispatch(
      changePasswordByUrl({
        uid,
        token: parsedToken,
        newPassword: newPassword,
        cnewPassword: cnewPassword,
      })
    );
  };

  if (success) {
    return (
      <section className="chg-pass-section d-flex justify-content-center align-items-center vh-100">
        <div className="chg-pass-container chg-pass-container-wide bg-white shadow-lg rounded d-flex flex-column flex-md-row">
          <div className="chg-pass-left chg-pass-status-left p-5 w-100 d-flex flex-column justify-content-center">
            <div style={{ marginTop: 8, marginBottom: 16 }}>
              <svg
                width="80"                           // ⬅ bumped from 64
                height="80"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
                <path
                  d="M7 12.5l3.2 3.2L17.5 8.5"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h3 className="chg-pass-title chg-pass-status-title">
              Password reset successful
            </h3>
            <p className="chg-pass-subtext chg-pass-status-subtext">
              You can now sign in with your new password.
            </p>
            <button
              className="btn chg-pass-btn w-100 chg-pass-status-btn"
              onClick={() => navigate("/")}
            >
              Back to Sign In
            </button>
          </div>

          <div className="chg-pass-right chg-pass-status-right p-5 w-100 d-flex justify-content-center align-items-center">
            <img
              src={icon}
              alt="Lock Icon"
              className="chg-pass-status-icon"
              style={{ opacity: 0.95 }}
            />
          </div>
        </div>
      </section>
    );
  }


  if (expired || (!uid || !parsedToken)) {
    return (
      <section className="chg-pass-section d-flex justify-content-center align-items-center vh-100">
        <div className="chg-pass-container chg-pass-container-wide bg-white shadow-lg rounded d-flex flex-column flex-md-row">
          <div className="chg-pass-left chg-pass-status-left p-5 w-100 d-flex flex-column justify-content-center">
            <h3 className="chg-pass-title chg-pass-status-title">
              Reset link expired or invalid
            </h3>
            <p className="chg-pass-subtext chg-pass-status-subtext">
              Your reset link may have expired or already been used. Request a new one.
            </p>
            <button
              className="btn chg-pass-btn w-100 chg-pass-status-btn"
              onClick={() => navigate("/forgot-password")}
            >
              Resend reset link
            </button>
            <p
              className="chg-pass-back text-center mt-3"
              style={{ cursor: "pointer" }}
              onClick={() => navigate("/")}
            >
              ← Back to Sign In
            </p>
          </div>

          <div className="chg-pass-right chg-pass-status-right p-5 w-100 d-flex justify-content-center align-items-center">
            <img
              src={icon}
              alt="Lock Icon"
              className="chg-pass-status-icon"
              style={{ opacity: 0.95 }}
            />
          </div>
        </div>
      </section>
    );
  }


  return (
    <>
      {loading && <LoadingScreen />}
      <section className="chg-pass-section d-flex justify-content-center align-items-center vh-100">
        <div className="chg-pass-container bg-white shadow-lg rounded d-flex flex-column flex-md-row">
          <div className="chg-pass-left p-4 w-100">
            <h3 className="chg-pass-title">Create a new password</h3>
            <p className="chg-pass-subtext">Enter and confirm your new password to finish resetting.</p>

            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="chg-pass-label" htmlFor="newPassword">New Password</label>
                <input
                  id="newPassword"
                  type="password"
                  className="chg-pass-input"
                  value={newPassword}
                  onChange={handlePasswordChange}
                  required
                />
              </div>

              <div className="mb-3">
                <label className="chg-pass-label" htmlFor="confirmPassword">Confirm New Password</label>
                <input
                  id="confirmPassword"
                  type="password"
                  className="chg-pass-input"
                  value={cnewPassword}
                  onChange={(e) => setCnewPassword(e.target.value)}
                  required
                />
              </div>

              {(localError || error) && (
                <div style={{ color: "#d9534f", fontSize: 13, marginTop: 6, marginBottom: 6 }}>
                  {localError || error}
                </div>
              )}

              <button type="submit" className="btn chg-pass-btn w-100" disabled={loading || disabled}>
                {loading ? "Saving..." : "Reset Password"}
              </button>

              <p className="chg-pass-back text-center mt-3" style={{ cursor: "pointer" }} onClick={() => navigate("/")}>
                ← Back to Sign In
              </p>
            </form>
          </div>

          <div className="chg-pass-right p-5 w-100">
            <div className="chg-pass-lock-icon">
              <img src={icon} alt="Lock Icon" className="chg-pass-lock-img" style={{ width: "120px", height: "120px" }} />
            </div>
            <h5 className="chg-pass-rules-title">Password Must:</h5>
            <ul className="list-unstyled">
              <li className={validation.length ? "valid" : ""}>✔ Contains at least 8 characters</li>
              <li className={validation.uppercase ? "valid" : ""}>✔ Contains at least One Uppercase (A-Z)</li>
              <li className={validation.number ? "valid" : ""}>✔ Contains at least One Number (0-9)</li>
              <li className={validation.specialChar ? "valid" : ""}>✔ Contains at least One Special Character ($#@%!_)</li>
            </ul>
          </div>
        </div>
      </section>
    </>
  );
};

export default ResetPassword;
